import axios from 'axios';
import I18n from 'i18n-js';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import TrueLinkLink from 'react/shared/components/true_link/main/TrueLinkLink';

export default function DashboardBanners({
  compromisedCard,
  displayDisputedCardBanner,
  isFamilyFunder,
  isViewOnly,
  fraudBlockedCard,
  accountId,
  status,
}) {
  let fraudBanner = null;
  let compromisedCardBanner = null;
  let disputedCardBanner = null;

  const viewOnlyBanner = (
    <p className="alert alert-warning alert--banner">
      You currently have read-only access to this True Link account. If you would like to make any
      changes, please contact a member of your organization with editing privileges.
    </p>
  );

  const familyFundingBanner = (
    <p className="alert alert-warning alert--banner">
      You currently have funding-only access to this True Link account. If you would like to make
      any changes to the Spending Settings, please contact a member of the organization with editing
      privileges.
    </p>
  );

  const reactivateCard = () => {
    const $alertDiv = $(document.createElement('div'));
    const $closeBtn = $(document.createElement('button'));
    $closeBtn.addClass('close').attr('data-dismiss', 'alert').text('x');
    $alertDiv.addClass('alert').append($closeBtn);

    axios
      .post(RailsRoutes.reactivate_card_dashboard_cards_path(), {
        account_id: accountId,
      })
      .then((response) => {
        if (response.data.success) {
          $('.alert').remove();
          $('.cardholder_name.pull-right').text('');
          $alertDiv
            .addClass('alert-success')
            .append('Your account will be reactivated momentarily!');
        } else {
          const email_link = $(document.createElement('a'))
            .attr({
              href: '/contact',
              target: '_blank',
            })
            .text('contact us');
          $alertDiv
            .addClass('alert-danger')
            .append('This card could not be automatically re-activated. Please ')
            .append(email_link)
            .append(' to resolve this issue');
        }
        $('.dashboard-header').after($alertDiv);
      });
  };

  const getFraudBlockedBanner = () => {
    if (fraudBlockedCard?.fraud_block_type === 'transaction_risk_support_required') {
      return (
        <div className="alert alert-danger alert--banner">
          <p>
            We've frozen this account due to suspicious activity. Please review all recent
            transactions and order a replacement Visa card. Contact us immediately for help filing a
            dispute claim if necessary.
          </p>
        </div>
      );
    } else if (fraudBlockedCard?.fraud_block_type === 'transaction_risk_self_serve') {
      return (
        <div className="alert alert-danger alert--banner">
          <p>
            We've frozen this account due to suspicious activity. Please review recent transactions.
            If all transactions are authorized, you can unfreeze the Visa card by{' '}
            <TrueLinkLink id="unblock_fraud_link" onClick={reactivateCard}>
              clicking here.
            </TrueLinkLink>{' '}
            If any transactions are unfamiliar or fraudulent, order a replacement card from the
            account tab and contact customer support immediately to open a dispute claim.
          </p>
        </div>
      );
    }
    return (
      <div className="alert alert-danger alert--banner">
        <p>
          To protect this account, we've temporarily set the card inactive. This may be due to
          several failed PIN transactions or many transactions with the same merchant in a short
          period. Please review the recent transactions. You can reactivate the card if all
          transactions were authorized by{' '}
          <TrueLinkLink id="unblock_fraud_link" onClick={reactivateCard}>
            clicking here
          </TrueLinkLink>{' '}
          or order a replacement card from the Account tab.
        </p>
      </div>
    );
  };

  const getCompromisedCardBanner = () => {
    if (compromisedCard?.status === 'INACTIVE') {
      return (
        <div className="alert alert-warning alert--banner">
          <p>
            We just got a report from Visa letting us know that the card information for{' '}
            {compromisedCard?.cardholder?.name} was compromised in a recent Visa data breach at a
            major national chain store. The card is currently inactive and immediate action is
            required.
          </p>
        </div>
      );
    } else if (compromisedCard?.status === 'OPEN' && !compromisedCard?.card_reissues) {
      return (
        <div className="alert alert-warning alert--banner">
          <p>
            We just got a report from Visa letting us know that the card information for{' '}
            {compromisedCard?.cardholder?.name} was compromised in a recent Visa data breach at a
            major national chain store. Immediate action is required. The card will be set inactive
            on {moment(compromisedCard?.mark_closed_on).format('MM/DD/YYYY')} at 9:00 AM PT.
          </p>
        </div>
      );
    }
  };

  const getDisputedCardBanner = () => (
    <div className="alert alert-warning alert--banner">
      <p>
        This card has been frozen because of a recently reported transaction dispute. Questions?{' '}
        {I18n.t('corp.admin_contact_message')}
      </p>
    </div>
  );

  if (isViewOnly) {
    fraudBanner = viewOnlyBanner;
  } else if (isFamilyFunder) {
    fraudBanner = familyFundingBanner;
  } else if (status === 'Temporary block' || fraudBlockedCard) {
    fraudBanner = getFraudBlockedBanner();
  }

  if (compromisedCard) {
    compromisedCardBanner = getCompromisedCardBanner();
  }

  if (displayDisputedCardBanner) {
    disputedCardBanner = getDisputedCardBanner();
  }

  return (
    <>
      {fraudBanner}
      {compromisedCardBanner}
      {disputedCardBanner}
    </>
  );
}

DashboardBanners.propTypes = {
  displayDisputedCardBanner: PropTypes.bool,
  isFamilyFunder: PropTypes.bool,
  isViewOnly: PropTypes.bool,
  fraudBlockedCard: PropTypes.object,
  compromisedCard: PropTypes.object,
  accountId: PropTypes.string,
  status: PropTypes.string,
};
